import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import { FaMoneyCheckAlt, FaSave, FaTimes } from "react-icons/fa";

const PaymentForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [sales, setSales] = useState([]);

  const [formData, setFormData] = useState({
    party_type: "Supplier",
    customer_id: "",
    supplier_id: "",
    sales_id: "",
    payment_method: "Cash",
    bank_id: "",
    cheque_number: "",
    cheque_date: "",
    amount: "",
    tax_percentage: "0",
    tax_amount: "0",
    payer: "Rekonic",
    payee: "",
    description: "",
    payment_date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [custRes, suppRes, bankRes] = await Promise.all([
          api.get("customers/"),
          api.get("suppliers/"),
          api.get("banks/"),
        ]);
        setCustomers(custRes.data);
        setSuppliers(suppRes.data);
        setBanks(bankRes.data);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.party_type === "Customer" && formData.customer_id) {
      const fetchSales = async () => {
        try {
          const res = await api.get(`sales/?customer_id=${formData.customer_id}`);
          setSales(res.data);
        } catch (err) {
          console.error("Error fetching sales:", err);
        }
      };
      fetchSales();
    } else {
      setSales([]);
    }
  }, [formData.customer_id, formData.party_type]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      
      // Auto calculate tax
      if (name === "amount" || name === "tax_percentage") {
        const amount = parseFloat(name === "amount" ? value : prev.amount) || 0;
        const taxPercent = parseFloat(name === "tax_percentage" ? value : prev.tax_percentage) || 0;
        updated.tax_amount = (amount * (taxPercent / 100)).toFixed(2);
      }

      // If party is selected, update payee/payer accordingly
      if (name === "customer_id" && formData.party_type === "Customer") {
        const cust = customers.find(c => c.id === parseInt(value));
        if (cust) updated.payee = cust.full_name || cust.name;
      }
      if (name === "supplier_id" && formData.party_type === "Supplier") {
        const supp = suppliers.find(s => s.id === parseInt(value));
        if (supp) updated.payee = supp.supplier_name || supp.name;
      }
      
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || !formData.payee) {
      alert("Amount and Payee are required");
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        ...formData,
        amount: Number(formData.amount),
        tax_percentage: Number(formData.tax_percentage),
        tax_amount: Number(formData.tax_amount),
        customer_id: formData.customer_id ? parseInt(formData.customer_id) : null,
        supplier_id: formData.supplier_id ? parseInt(formData.supplier_id) : null,
        sales_id: formData.sales_id ? parseInt(formData.sales_id) : null,
        bank_id: formData.bank_id ? parseInt(formData.bank_id) : null,
        payment_date: formData.payment_date ? new Date(formData.payment_date).toISOString() : null,
        cheque_date: formData.cheque_date ? new Date(formData.cheque_date).toISOString() : null,
      };
      
      await api.post("payment_vouchers/", payload);
      alert("Payment Voucher saved successfully!");
      navigate("/dashboard/payments/list");
    } catch (err) {
      console.error("Error saving payment voucher:", err);
      alert("Failed to save payment voucher. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4 max-w-2xl mx-auto">
      <div className="card shadow-sm border-0">
        <div className="card-header bg-primary text-white d-flex align-items-center gap-2">
          <FaMoneyCheckAlt />
          <h4 className="mb-0">Add New Payment Voucher</h4>
        </div>
        <div className="card-body p-4">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Payment Date</label>
                <input
                  type="date"
                  name="payment_date"
                  className="form-control"
                  value={formData.payment_date}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Party Type</label>
                <select
                  name="party_type"
                  className="form-select"
                  value={formData.party_type}
                  onChange={handleChange}
                >
                  <option value="Supplier">Supplier</option>
                  <option value="Customer">Customer</option>
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">
                {formData.party_type === "Customer" ? "Select Customer" : "Select Supplier"}
              </label>
              {formData.party_type === "Customer" ? (
                <select
                  name="customer_id"
                  className="form-select"
                  value={formData.customer_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Select Customer --</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>{c.full_name || c.name}</option>
                  ))}
                </select>
              ) : (
                <select
                  name="supplier_id"
                  className="form-select"
                  value={formData.supplier_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Select Supplier --</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>{s.supplier_name || s.name}</option>
                  ))}
                </select>
              )}
            </div>

            {formData.party_type === "Customer" && (
              <div className="mb-3">
                <label className="form-label fw-bold">Sales ID (Optional)</label>
                <select
                  name="sales_id"
                  className="form-select"
                  value={formData.sales_id}
                  onChange={handleChange}
                >
                  <option value="">-- Select Sale --</option>
                  {sales.map((s) => (
                    <option key={s.id} value={s.id}>Sale #{s.id} - {s.grand_total}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="mb-3">
              <label className="form-label fw-bold">Payment Method</label>
              <select
                name="payment_method"
                className="form-select"
                value={formData.payment_method}
                onChange={handleChange}
              >
                <option value="Cash">Cash</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cheque">Cheque</option>
                <option value="Credit Card">Credit Card</option>
              </select>
            </div>

            {(formData.payment_method === "Bank Transfer" || formData.payment_method === "Credit Card") && (
              <div className="mb-3">
                <label className="form-label fw-bold">Select Bank</label>
                <select
                  name="bank_id"
                  className="form-select"
                  value={formData.bank_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Select Bank --</option>
                  {banks.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
            )}

            {formData.payment_method === "Cheque" && (
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Cheque Number</label>
                  <input
                    type="text"
                    name="cheque_number"
                    className="form-control"
                    value={formData.cheque_number}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Cheque Date</label>
                  <input
                    type="date"
                    name="cheque_date"
                    className="form-control"
                    value={formData.cheque_date}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            )}

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold text-danger">Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="amount"
                  className="form-control"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  placeholder="0.00"
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label fw-bold">Tax (%)</label>
                <input
                  type="number"
                  step="0.01"
                  name="tax_percentage"
                  className="form-control"
                  value={formData.tax_percentage}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label fw-bold">Tax Amount</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.tax_amount}
                  readOnly
                  disabled
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Payer *</label>
                <input
                  type="text"
                  name="payer"
                  className="form-control"
                  value={formData.payer}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Payee *</label>
                <input
                  type="text"
                  name="payee"
                  className="form-control"
                  value={formData.payee}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="form-label fw-bold">Description</label>
              <textarea
                name="description"
                className="form-control"
                rows="3"
                value={formData.description}
                onChange={handleChange}
                placeholder="Payment details..."
              ></textarea>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary d-flex align-items-center gap-2"
                onClick={() => navigate("/dashboard/payments/list")}
              >
                <FaTimes /> Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary d-flex align-items-center gap-2"
                disabled={loading}
              >
                <FaSave /> {loading ? "Saving..." : "Save Voucher"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PaymentForm;
